import { __awaiter, __extends, __generator } from "tslib";
import { ClientCacheState, ConfigServiceBase } from "./ConfigServiceBase";
import { AbortToken, delay, getMonotonicTimeMs } from "./Utils";
export var POLL_EXPIRATION_TOLERANCE_MS = 500;
var AutoPollConfigService = /** @class */ (function (_super) {
    __extends(AutoPollConfigService, _super);
    function AutoPollConfigService(configFetcher, options) {
        var _this = _super.call(this, configFetcher, options) || this;
        _this.signalInitialization = function () { };
        _this.stopToken = new AbortToken();
        _this.pollIntervalMs = options.pollIntervalSeconds * 1000;
        // Due to the inaccuracy of the timer, some tolerance should be allowed when checking for
        // cache expiration in the polling loop, otherwise some fetch operations may be missed.
        _this.pollExpirationMs = _this.pollIntervalMs - POLL_EXPIRATION_TOLERANCE_MS;
        var initialCacheSyncUp = _this.syncUpWithCache();
        if (options.maxInitWaitTimeSeconds !== 0) {
            _this.initialized = false;
            // This promise will be resolved as soon as
            // 1. a cache sync operation completes, and the obtained config is up-to-date (see getConfig and startRefreshWorker),
            // 2. or, in case the client is online and the internal cache is still empty or expired after the initial cache sync-up,
            //    the first config fetch operation completes, regardless of success or failure (see onConfigFetched).
            var initSignalPromise = new Promise(function (resolve) { return _this.signalInitialization = resolve; });
            // This promise will be resolved when either initialization ready is signalled by signalInitialization() or maxInitWaitTimeSeconds pass.
            _this.initializationPromise = _this.waitForInitializationAsync(initSignalPromise).then(function (success) {
                _this.initialized = true;
                return success;
            });
        }
        else {
            _this.initialized = true;
            _this.initializationPromise = Promise.resolve(false);
        }
        _this.readyPromise = _this.getReadyPromise(_this.initializationPromise, function (initializationPromise) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, initializationPromise];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, this.getCacheState(this.options.cache.getInMemory())];
                }
            });
        }); });
        _this.startRefreshWorker(initialCacheSyncUp, _this.stopToken);
        return _this;
    }
    AutoPollConfigService.prototype.waitForInitializationAsync = function (initSignalPromise) {
        return __awaiter(this, void 0, void 0, function () {
            var abortToken, success;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(this.options.maxInitWaitTimeSeconds < 0)) return [3 /*break*/, 2];
                        return [4 /*yield*/, initSignalPromise];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, true];
                    case 2:
                        abortToken = new AbortToken();
                        return [4 /*yield*/, Promise.race([
                                initSignalPromise.then(function () { return true; }),
                                delay(this.options.maxInitWaitTimeSeconds * 1000, abortToken).then(function () { return false; })
                            ])];
                    case 3:
                        success = _a.sent();
                        abortToken.abort();
                        return [2 /*return*/, success];
                }
            });
        });
    };
    AutoPollConfigService.prototype.getConfig = function () {
        return __awaiter(this, void 0, void 0, function () {
            var cachedConfig;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.options.logger.debug("AutoPollConfigService.getConfig() called.");
                        return [4 /*yield*/, this.syncUpWithCache()];
                    case 1:
                        cachedConfig = _a.sent();
                        if (!!cachedConfig.isExpired(this.pollIntervalMs)) return [3 /*break*/, 2];
                        this.signalInitialization();
                        return [3 /*break*/, 5];
                    case 2:
                        if (!(!this.isOffline && !this.initialized)) return [3 /*break*/, 4];
                        this.options.logger.debug("AutoPollConfigService.getConfig() - cache is empty or expired, waiting for initialization.");
                        return [4 /*yield*/, this.initializationPromise];
                    case 3:
                        _a.sent();
                        cachedConfig = this.options.cache.getInMemory();
                        return [3 /*break*/, 5];
                    case 4:
                        this.options.logger.debug("AutoPollConfigService.getConfig() - cache is empty or expired.");
                        return [2 /*return*/, cachedConfig];
                    case 5:
                        this.options.logger.debug("AutoPollConfigService.getConfig() - returning value from cache.");
                        return [2 /*return*/, cachedConfig];
                }
            });
        });
    };
    AutoPollConfigService.prototype.refreshConfigAsync = function () {
        this.options.logger.debug("AutoPollConfigService.refreshConfigAsync() called.");
        return _super.prototype.refreshConfigAsync.call(this);
    };
    AutoPollConfigService.prototype.dispose = function () {
        this.options.logger.debug("AutoPollConfigService.dispose() called.");
        _super.prototype.dispose.call(this);
        if (!this.stopToken.aborted) {
            this.stopRefreshWorker();
        }
    };
    AutoPollConfigService.prototype.onConfigFetched = function (newConfig) {
        _super.prototype.onConfigFetched.call(this, newConfig);
        this.signalInitialization();
    };
    AutoPollConfigService.prototype.goOnline = function () {
        // We need to restart the polling loop because going from offline to online should trigger a refresh operation
        // immediately instead of waiting for the next tick (which might not happen until much later).
        this.stopRefreshWorker();
        this.stopToken = new AbortToken();
        this.startRefreshWorker(null, this.stopToken);
    };
    AutoPollConfigService.prototype.startRefreshWorker = function (initialCacheSyncUp, stopToken) {
        return __awaiter(this, void 0, void 0, function () {
            var scheduledNextTimeMs, err_1, realNextTimeMs, err_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.options.logger.debug("AutoPollConfigService.startRefreshWorker() called.");
                        _a.label = 1;
                    case 1:
                        if (!!stopToken.aborted) return [3 /*break*/, 11];
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 9, , 10]);
                        scheduledNextTimeMs = getMonotonicTimeMs() + this.pollIntervalMs;
                        _a.label = 3;
                    case 3:
                        _a.trys.push([3, 5, , 6]);
                        return [4 /*yield*/, this.refreshWorkerLogic(initialCacheSyncUp)];
                    case 4:
                        _a.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        err_1 = _a.sent();
                        this.options.logger.autoPollConfigServiceErrorDuringPolling(err_1);
                        return [3 /*break*/, 6];
                    case 6:
                        realNextTimeMs = scheduledNextTimeMs - getMonotonicTimeMs();
                        if (!(realNextTimeMs > 0)) return [3 /*break*/, 8];
                        return [4 /*yield*/, delay(realNextTimeMs, stopToken)];
                    case 7:
                        _a.sent();
                        _a.label = 8;
                    case 8: return [3 /*break*/, 10];
                    case 9:
                        err_2 = _a.sent();
                        this.options.logger.autoPollConfigServiceErrorDuringPolling(err_2);
                        return [3 /*break*/, 10];
                    case 10:
                        initialCacheSyncUp = null; // allow GC to collect the Promise and its result
                        return [3 /*break*/, 1];
                    case 11: return [2 /*return*/];
                }
            });
        });
    };
    AutoPollConfigService.prototype.stopRefreshWorker = function () {
        this.options.logger.debug("AutoPollConfigService.stopRefreshWorker() called.");
        this.stopToken.abort();
    };
    AutoPollConfigService.prototype.refreshWorkerLogic = function (initialCacheSyncUp) {
        return __awaiter(this, void 0, void 0, function () {
            var latestConfig;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.options.logger.debug("AutoPollConfigService.refreshWorkerLogic() - called.");
                        return [4 /*yield*/, (initialCacheSyncUp !== null && initialCacheSyncUp !== void 0 ? initialCacheSyncUp : this.syncUpWithCache())];
                    case 1:
                        latestConfig = _a.sent();
                        if (!latestConfig.isExpired(this.pollExpirationMs)) return [3 /*break*/, 3];
                        if (!(initialCacheSyncUp ? !this.isOfflineExactly : !this.isOffline)) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.refreshConfigCoreAsync(latestConfig)];
                    case 2:
                        _a.sent();
                        return [2 /*return*/]; // postpone signalling initialization until `onConfigFetched`
                    case 3:
                        this.signalInitialization();
                        return [2 /*return*/];
                }
            });
        });
    };
    AutoPollConfigService.prototype.getCacheState = function (cachedConfig) {
        if (cachedConfig.isEmpty) {
            return ClientCacheState.NoFlagData;
        }
        if (cachedConfig.isExpired(this.pollIntervalMs)) {
            return ClientCacheState.HasCachedFlagDataOnly;
        }
        return ClientCacheState.HasUpToDateFlagData;
    };
    return AutoPollConfigService;
}(ConfigServiceBase));
export { AutoPollConfigService };
